import React from 'react';
import type { FormResponse } from '@troia-v3/types';
import { toast } from 'sonner';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { getFormStatusMeta } from './formStatusMeta';

export type FormCardAction = 'edit' | 'duplicate' | 'delete' | 'report' | 'copyLink' | 'toggleStatus';

export interface FormCardProps {
  form: FormResponse;
  onAction: (action: FormCardAction, form: FormResponse) => void;
}

/**
 * Card de formulário (view "Cards"). Padrão-lista (F34): SEM checkbox — seleção
 * em massa vive só na view de tabela. Menu ••• de ações.
 */
export const FormCard: React.FC<FormCardProps> = ({ form, onAction }) => {
  const status = getFormStatusMeta(form.status);
  const isActive = form.status === 'active';
  const fieldsCount = form.fields?.length || 0;

  const copyLink = () => {
    const url = `${window.location.origin}/f/${form.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };

  const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString('pt-BR') : '—');

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => onAction('edit', form) },
    { label: 'Relatório', icon: 'bar-chart', onClick: () => onAction('report', form) },
    { label: 'Copiar link', icon: 'link', onClick: copyLink },
    { label: 'Duplicar', icon: 'copy', onClick: () => onAction('duplicate', form) },
    {
      label: isActive ? 'Desativar' : 'Ativar',
      icon: isActive ? 'toggle-left' : 'toggle-right',
      onClick: () => onAction('toggleStatus', form),
    },
    { label: 'Excluir', icon: 'trash', onClick: () => onAction('delete', form), variant: 'danger' },
  ];

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`form-card-${form.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-[11px] flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(59,130,246,.13)', color: '#3B82F6' }}
        >
          <Icon name="file-text" className="w-5 h-5" />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {form.name}
          </h3>
          {form.description && (
            <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--theme-muted-text)' }}>
              {form.description}
            </p>
          )}
        </div>

        <div className="flex-shrink-0 -mr-1 -mt-1">
          <CardActionsMenu actions={actions} size="sm" data-testid={`form-actions-${form.id}`} />
        </div>
      </div>

      {/* Stats + status */}
      <div className="mt-3 flex items-center gap-5">
        <div>
          <span className="block text-base font-bold tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
            {form.responsesCount}
          </span>
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Respostas</span>
        </div>
        <div>
          <span className="block text-base font-bold tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
            {fieldsCount}
          </span>
          <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Campos</span>
        </div>
        <StatusPill
          variant={status.variant}
          label={status.label}
          className="ml-auto self-center"
          data-testid={`form-status-${form.id}`}
        />
      </div>

      {/* Footer: slug + data */}
      <div
        className="mt-3 pt-3 flex items-center gap-1.5 text-xs"
        style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
      >
        <Icon name="external-link" className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate">/f/{form.slug}</span>
        <span className="ml-auto flex-shrink-0">{formatDate(form.createdAt as unknown as string)}</span>
      </div>
    </div>
  );
};

FormCard.displayName = 'FormCard';
